import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SwindonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Swindon Pothole Claims | 14,411 Potholes Filled in One Year | Fixtyer"
        description="Swindon Borough Council filled 14,411 potholes in 2023/24 — more than double the 2020/21 figure — while its own report admits capital funding was roughly a third of that needed to halt decline. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/swindon"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Swindon', url: '/councils/swindon' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Swindon spends more than double its DfT allocation — why is the spend scorecard still AMBER?',
            answer: 'The DfT rates Swindon AMBER on spend because aggregate investment has not kept pace with network need. Swindon\'s own transparency report states historic capital funding for major carriageway maintenance was "roughly a third of that deemed necessary" to hold condition steady. Your claim still turns on Section 58 and the specific defect — not the scorecard colour alone.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 647km — 73% of Swindon\'s 886km network — and are surveyed using Coarse Visual Inspection rather than SCANNER, with around half the network measured each year. RED-condition U-roads rose from 9% in 2020–2022 to 13% in 2023 before easing to 11% in 2024. That two-year survey cycle creates gaps in network-level condition knowledge under Section 58.',
          },
          {
            question: 'Does Swindon\'s 30mm pothole threshold affect my claim?',
            answer: 'Swindon\'s repair guidance states a pothole must be at least 30mm deep to be classed as a safety defect, with those over 40mm repaired the next working day. Around 30% of public reports fall outside standard investigation criteria. Liability under Section 41 is not defined by the repair threshold — prior reports, photos showing defect age, and evidence the pothole posed a hazard before your incident all matter.',
          },
          {
            question: 'Why did pothole fills peak at 14,411 in 2023/24?',
            answer: 'Swindon filled 14,411 potholes in 2023/24 — up from 6,000 in 2020/21 and 7,932 in 2022/23 — the same period its own report describes as a trend of gradual condition deterioration across all road classes. When reactive filling spikes while B/C green condition falls from 71% to 65%, defects are forming faster than structural renewal can keep pace.',
          },
          {
            question: 'The M4 and A419 are not Swindon\'s responsibility — who do I claim against?',
            answer: 'Swindon Borough Council\'s transparency report explicitly states it is not responsible for the M4 motorway or A419 — National Highways maintains those routes. If your damage was on either road, report it to National Highways and pursue your claim against them. Swindon\'s data covers HMPE roads within the borough only.',
          },
          {
            question: 'Does the council\'s admission about under-funding help my claim?',
            answer: 'It provides useful context. Swindon\'s June 2025 report states the primary cause of five-year deterioration is "historic low levels of capital funding" previously calculated at roughly a third of that needed for steady-state condition. That is documented knowledge of systemic under-investment — which informs what a "reasonable" inspection and repair system should achieve on a 886km network producing 45,073 pothole fills in five years.',
          },
          {
            question: 'Do I need to report the pothole before claiming?',
            answer: 'Swindon encourages residents to report potholes online; reports receive an automated acknowledgement with a unique tracking code and a follow-up email on the investigation outcome. Reporting creates a dated council record — useful if the defect was known before your incident, or if Swindon failed to repair a qualifying pothole within its stated timescales (next working day for 40mm+ defects).',
          },
        ]}
      />
      <ServiceSchema
        councilName="Swindon Borough Council"
        councilSlug="swindon"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/swindon"
        headline="Swindon Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Swindon</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Swindon: 14,411 potholes filled in a single year
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Swindon Borough Council filled{' '}
            <strong className="text-rose-400">14,411 potholes in 2023/24</strong> — more than double the 6,000
            recorded in 2020/21 — while its own transparency report describes{' '}
            <strong className="text-amber-400">gradual condition deterioration across all road classes</strong>{' '}
            over five years. The DfT rates overall maintenance, condition, spend and best practice{' '}
            <strong className="text-amber-400">AMBER</strong> for 2025/26. Historic capital funding, the council
            states, was roughly{' '}
            <strong className="text-rose-400">a third of that deemed necessary</strong> to hold the network steady.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">14,411</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2023/24 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 6,000 in 2020/21 and 7,932 in 2022/23 on an 886km network where U-roads — 647km, 73% of
              all carriageways — saw RED condition rise to 13% in 2023.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and Coarse Visual Inspection data from Swindon Borough Council&apos;s June 2025
            transparency report — deterioration across every road class
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (74km — 8.4% of network): worsening at the red end</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.0%</td>
                    <td className="px-4 py-2 text-center">22.9%</td>
                    <td className="px-4 py-2 text-center">75.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">25.7%</td>
                    <td className="px-4 py-2 text-center">71.5%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.4%</td>
                    <td className="px-4 py-2 text-center">23.1%</td>
                    <td className="px-4 py-2 text-center">74.5%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">26.6%</td>
                    <td className="px-4 py-2 text-center">70.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.7%</td>
                    <td className="px-4 py-2 text-center font-bold">25.7%</td>
                    <td className="px-4 py-2 text-center font-bold">70.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads are surveyed annually under SCANNER, but RED condition has nearly doubled from 2.0% to 3.7%
              since 2020. Green A-roads have fallen from 75.1% to 70.6%.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (165km — 18.6% of network): green share falling</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.0%</td>
                    <td className="px-4 py-2 text-center">25.0%</td>
                    <td className="px-4 py-2 text-center">71.0%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">23.9%</td>
                    <td className="px-4 py-2 text-center">72.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">24.0%</td>
                    <td className="px-4 py-2 text-center">72.3%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.7%</td>
                    <td className="px-4 py-2 text-center">26.9%</td>
                    <td className="px-4 py-2 text-center">68.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.4%</td>
                    <td className="px-4 py-2 text-center font-bold">29.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">65.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B and C roads are surveyed over a two-year cycle — around half the network each year. RED condition
              has risen from 4.0% to 5.4%, amber from 25.0% to 29.6%, and green has fallen from 71.0% to 65.0%.
              Over a third of the classified local network now needs — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">DfT AMBER spend on an under-funded network</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£2.787m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£5.761m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">a third</div>
                <div className="text-brand-300 text-sm">Of steady-state funding historically received</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Swindon projects more than double its DfT capital allocation in 2025/26, with an estimated{' '}
              <span className="text-amber-400">80% preventative share</span>. Yet the DfT still rates spend{' '}
              <span className="text-amber-400">AMBER</span> — and the council&apos;s own report attributes
              deterioration to historic capital levels previously calculated as roughly a third of that needed to
              prevent further decline. In five years to 2024/25, just 45.1km of carriageway received resurfacing
              or surface treatment on an 886km network.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 647km residential majority
          </h2>
          <p className="text-surface-600 mb-8">
            73% of Swindon&apos;s network is unclassified roads — surveyed by Coarse Visual Inspection, not SCANNER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Coarse Visual Inspection and the two-year cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                Swindon surveys U-roads using Coarse Visual Inspection — a different methodology from the SCANNER
                laser surveys used on classified roads. Data is collected over a two-year period, with around half
                the unclassified network surveyed each year.
              </p>
              <p className="text-surface-600 text-sm">
                At 13% RED in 2023, roughly <strong>84km of residential and estate roads</strong> were in the worst
                condition category — on a road type where most Swindon motorists live and park.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 from 2026/27</h3>
              <p className="text-amber-800 text-sm mb-3">
                Swindon&apos;s report notes that from 2026/27 a new BSI PAS 2161 methodology will replace the current
                three-category RED/amber/green system with five condition bands nationwide.
              </p>
              <p className="text-amber-800 text-sm">
                Until then, the published five-year trend is directly comparable — and the council&apos;s own words
                describe it as gradual deterioration across all road classes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Swindon must show a reasonable system for knowing road condition.
              For the 647km U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last Coarse Visual Inspection-surveyed — and was it in a surveyed half that year?</li>
              <li>• If 11–13% of U-roads were RED at recent surveys, what was done about yours?</li>
              <li>• Does a two-year survey cycle on 73% of the network meet the standard for knowing defect risk?</li>
              <li>• Can the council prove inspection frequency matched deterioration on a network filling 45,073 potholes in five years?</li>
            </ul>
            <p className="text-white font-medium">
              A borough producing 14,411 pothole fills in a single year is, by definition, a network where defects
              routinely form between inspections.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            45,073 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Swindon Borough Council&apos;s own transparency report — and what the spike tells you
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">6,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,500</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">7,932</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,411</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">10,230</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">45,073</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~25 defects a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Swindon fills around 25 potholes per day. For 2025/26 the council
                estimates 9,000 fills — still well above the 2020/21 baseline. A network producing defects at
                this rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Structural treatment lagging behind</h3>
              <p className="text-amber-800 text-sm">
                In the five years to 2024/25, Swindon treated 45.1km of carriageway with resurfacing and surface
                treatments — roughly 5% of the 886km network. The 2025/26 programme adds a further 13.5km (8.4
                miles). Reactive filling at scale without matching structural renewal is exactly the pattern the
                council links to gradual deterioration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The funding admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The council&apos;s own explanation
          </h2>
          <p className="text-surface-600 mb-8">
            Swindon Borough Council on why conditions have slipped — and what it plans to review
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The condition of local roads in Swindon Borough have been on a trend of gradual condition deterioration over the past 5-years. This trend has been fairly similar across all road classes. The primary cause of this deterioration is deemed to be the historic low levels of capital funding available annually for major carriageway maintenance in the Borough, previously calculated as being roughly a third of that deemed necessary to keep the road condition at a steady state."
              source="Swindon Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="It remains key to the Council's asset-driven funding approach that maintenance schemes continue to be selected on a needs-led basis in line with the Asset Management Strategy. However, it is recognised that there is community concern regarding highway defects that are outside the intervention criteria set out in the policy and are not therefore being addressed. The Council will therefore further review the Highway Asset Management Policy in light of these concerns."
              source="Swindon Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Swindon formally acknowledges five-year deterioration and links it to under-funding of major
                carriageway works. It also recognises defects below its intervention threshold that are not being
                addressed — the same class of defect that may still cause vehicle damage.
              </p>
              <p className="text-surface-600 text-sm">
                Documented systemic under-investment informs what a &ldquo;reasonable&rdquo; maintenance system should
                achieve on an £1.8bn highway asset.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Did your pothole meet Swindon&apos;s 30mm safety-defect threshold at the time of your incident?</li>
                <li>• Was it reported before you hit it — and if so, was the 40mm next-day repair standard met?</li>
                <li>• If the defect was below intervention criteria, was it still a known hazard on a RED-surveyed road?</li>
                <li>• Was your road on the 2025/26 resurfacing programme (3km A, 5.75km B, 750m C, 4km U)?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER across the board means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an all-AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Swindon scores AMBER on every DfT scorecard for 2025/26 — here is how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ISO-aligned asset management policy approved February 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads surveyed annually under SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented intervention policy and streetworks permit scheme since 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 40mm+ potholes targeted for next-working-day repair
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-inspected A-roads with clear repair logs.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — deterioration across A, B/C and U-roads since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER spend — historic capital at roughly a third of steady-state need
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 647km U-roads on two-year Coarse Visual Inspection cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 45,073 pothole fills in five years — defects forming faster than renewal
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council reviewing policy because defects below intervention criteria go unaddressed
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              With AMBER across every DfT scorecard, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Swindon online reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the two-year survey gap and 11–13% RED share are structural arguments</li>
              <li>• Whether the pothole met Swindon&apos;s 30mm/40mm thresholds and repair timescales at the time of your incident</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Swindon&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Swindon first
          </h2>
          <p className="text-surface-600 mb-6">
            Swindon encourages residents to report potholes online. Reports receive an automated acknowledgement
            with a unique tracking code and a follow-up email on the investigation outcome. Potholes at least 30mm
            deep are risk-assessed as safety defects; those over 40mm are repaired the next working day. Around 30%
            of public reports fall outside standard investigation criteria — keep your reference number regardless.
          </p>
          <a
            href="https://www.swindon.gov.uk/info/20040/road_safety_maintenance_and_repairs/339/repairing_swindons_roads"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Swindon Borough Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            If the pothole is on the M4 or A419, report it to National Highways instead. Keep your reference number
            and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Swindon?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-AMBER council still owes a duty of care on the specific defect. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No two-year U-road survey gap argument</li>
                <li>• No 14,411 pothole peak cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Five-year deterioration and under-funding admission documented</li>
                <li>✅ U-road RED rise and Coarse Visual Inspection cycle argued</li>
                <li>✅ 45,073 pothole fills and 30mm/40mm threshold context cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Swindon</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=swindon"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Swindon claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Swindon spends more than double its DfT allocation — why is the spend scorecard still AMBER?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT rates Swindon AMBER on spend because aggregate investment has not kept pace with network
                need. Swindon&apos;s own transparency report states historic capital funding for major carriageway
                maintenance was &ldquo;roughly a third of that deemed necessary&rdquo; to hold condition steady.
                Your claim still turns on Section 58 and the specific defect — not the scorecard colour alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 647km — 73% of Swindon&apos;s 886km network — and are surveyed using
                Coarse Visual Inspection rather than SCANNER, with around half the network measured each year.
                RED-condition U-roads rose from 9% in 2020–2022 to 13% in 2023 before easing to 11% in 2024.
                That two-year survey cycle creates gaps in network-level condition knowledge under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Swindon&apos;s 30mm pothole threshold affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Swindon&apos;s repair guidance states a pothole must be at least 30mm deep to be classed as a safety
                defect, with those over 40mm repaired the next working day. Around 30% of public reports fall outside
                standard investigation criteria. Liability under Section 41 is not defined by the repair threshold —
                prior reports, photos showing defect age, and evidence the pothole posed a hazard before your
                incident all matter.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole fills peak at 14,411 in 2023/24?
              </h3>
              <p className="text-surface-600 text-sm">
                Swindon filled 14,411 potholes in 2023/24 — up from 6,000 in 2020/21 and 7,932 in 2022/23 — the
                same period its own report describes as a trend of gradual condition deterioration across all road
                classes. When reactive filling spikes while B/C green condition falls from 71% to 65%, defects are
                forming faster than structural renewal can keep pace.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The M4 and A419 are not Swindon&apos;s responsibility — who do I claim against?
              </h3>
              <p className="text-surface-600 text-sm">
                Swindon Borough Council&apos;s transparency report explicitly states it is not responsible for the M4
                motorway or A419 — National Highways maintains those routes. If your damage was on either road,
                report it to National Highways and pursue your claim against them. Swindon&apos;s data covers HMPE
                roads within the borough only.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council&apos;s admission about under-funding help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It provides useful context. Swindon&apos;s June 2025 report states the primary cause of five-year
                deterioration is &ldquo;historic low levels of capital funding&rdquo; previously calculated at roughly
                a third of that needed for steady-state condition. That is documented knowledge of systemic
                under-investment — which informs what a &ldquo;reasonable&rdquo; inspection and repair system should
                achieve on an 886km network producing 45,073 pothole fills in five years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Swindon encourages residents to report potholes online; reports receive an automated acknowledgement
                with a unique tracking code and a follow-up email on the investigation outcome. Reporting creates a
                dated council record — useful if the defect was known before your incident, or if Swindon failed to
                repair a qualifying pothole within its stated timescales (next working day for 40mm+ defects).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.swindon.gov.uk/directory_record/25607/local_highways_maintenance_transparency_report/category/234/what_our_priorities_are_and_how_we_are_doing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Swindon Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Pothole repair thresholds and reporting guidance from{' '}
            <a
              href="https://www.swindon.gov.uk/info/20040/road_safety_maintenance_and_repairs/339/repairing_swindons_roads/2"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Swindon Borough Council — Repairing Swindon&apos;s roads
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
